import DOMPurify from 'dompurify';
import type { RaRecord } from 'react-admin';

/**
 * Pełna struktura maila na ekranie (03.11.2026, przełącznik `emailDetails`,
 * S-03.11-02, paczka CS i Administracja). API od tego dnia oddaje
 * uczestników z rolami (`participants`, z zapasem na stare kolumny po
 * stronie serwera), weryfikację nadawcy i strukturę załączników — tu tylko
 * je nazywamy i przygotowujemy treść HTML do bezpiecznego pokazania.
 */

export const FEATURE_EMAIL_DETAILS = 'emailDetails';

export type ParticipantRole = 'FROM' | 'SENDER' | 'TO' | 'CC' | 'BCC' | 'REPLY_TO';

export interface Participant {
  role: ParticipantRole;
  address: string;
  name: string | null;
  position: number;
}

/** Nazwy ról jak w polskim programie pocztowym. */
export const PARTICIPANT_ROLE_LABELS: Record<ParticipantRole, string> = {
  FROM: 'Od',
  SENDER: 'Wysłane w imieniu przez',
  TO: 'Do',
  CC: 'DW',
  BCC: 'UDW',
  REPLY_TO: 'Odpowiedz do',
};

export const PARTICIPANT_ROLE_ORDER: ParticipantRole[] = ['FROM', 'SENDER', 'TO', 'CC', 'BCC', 'REPLY_TO'];

/**
 * Uczestnicy rekordu. Odpowiedź API sprzed 03.11.2026 (albo z pamięci
 * react-admina) nie ma `participants` — wtedy składamy ich ze starych pól,
 * jak serwer.
 */
export function participantsOf(record: RaRecord): Participant[] {
  const list = record.participants as Participant[] | undefined;
  if (Array.isArray(list)) return list;
  const out: Participant[] = [];
  if (record.fromAddress) {
    out.push({ role: 'FROM', address: String(record.fromAddress), name: (record.fromName as string) || null, position: 0 });
  }
  ((record.toAddresses as string[] | undefined) ?? []).forEach((a, i) => out.push({ role: 'TO', address: a, name: null, position: i }));
  ((record.ccAddresses as string[] | undefined) ?? []).forEach((a, i) => out.push({ role: 'CC', address: a, name: null, position: i }));
  return out;
}

export function participantsByRole(list: Participant[], role: ParticipantRole): Participant[] {
  return list.filter((p) => p.role === role).sort((a, b) => a.position - b.position);
}

/** „Anna Nowak <anna@…>” albo sam adres. */
export function participantText(p: Participant): string {
  return p.name ? `${p.name} <${p.address}>` : p.address;
}

/**
 * Skrót odbiorców na liście: pierwszy z „Do”, potem „+n”. Tekst pełny idzie
 * do podpowiedzi.
 */
export function recipientsSummary(list: Participant[]): { short: string; full: string } {
  const to = participantsByRole(list, 'TO');
  const cc = participantsByRole(list, 'CC');
  const all = [...to, ...cc];
  if (all.length === 0) return { short: '—', full: '' };
  const first = all[0];
  const rest = all.length - 1;
  const short = `${first.name || first.address}${rest > 0 ? ` +${rest}` : ''}`;
  const full = [
    to.length ? `Do: ${to.map(participantText).join(', ')}` : '',
    cc.length ? `DW: ${cc.map(participantText).join(', ')}` : '',
  ]
    .filter(Boolean)
    .join('\n');
  return { short, full };
}

export const IMPORTANCE_LABELS: Record<string, string> = { low: 'niska', normal: 'normalna', high: 'wysoka' };

export interface SenderVerification {
  status: 'failed' | 'passed' | 'unknown';
  failed: string[];
  results: Record<string, string | null>;
}

/** Zdanie ostrzeżenia albo `null`, gdy nie ma czego zgłosić. */
export function senderWarning(v: SenderVerification | undefined | null): string | null {
  if (!v || v.status !== 'failed' || v.failed.length === 0) return null;
  return `Nadawca nie przeszedł weryfikacji (${v.failed.join(', ')}). Sprawdź, czy mail na pewno jest od tego klienta, zanim na jego podstawie coś zmienisz.`;
}

// ── załączniki ──────────────────────────────────────────────────────────────

export interface AttachmentNode {
  att: RaRecord;
  depth: number;
}

/**
 * Załączniki w kolejności do pokazania: każdy plik, a pod nim pliki z jego
 * wnętrza (mail w mailu, plik w ZIP), z głębokością. Rodzic spoza listy —
 * plik na pierwszym poziomie (nie gubimy go); pętla w danych niczego nie
 * zawiesza.
 */
export function attachmentTree(list: RaRecord[]): AttachmentNode[] {
  const ids = new Set(list.map((a) => a.id as number));
  const children = new Map<number, RaRecord[]>();
  const roots: RaRecord[] = [];
  for (const a of list) {
    const parent = a.parentAttachmentId as number | null | undefined;
    if (parent != null && ids.has(parent) && parent !== a.id) {
      children.set(parent, [...(children.get(parent) ?? []), a]);
    } else roots.push(a);
  }
  const out: AttachmentNode[] = [];
  const seen = new Set<number>();
  const walk = (a: RaRecord, depth: number) => {
    const id = a.id as number;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ att: a, depth });
    for (const c of children.get(id) ?? []) walk(c, depth + 1);
  };
  roots.forEach((r) => walk(r, 0));
  // Pliki tylko w pętli (rodzic → dziecko → rodzic) — też na listę.
  list.forEach((a) => walk(a, 0));
  return out;
}

/** Content-ID w postaci do porównań: bez `<>`, małymi literami. */
export function normalizeContentId(v: string | null | undefined): string {
  return String(v ?? '')
    .trim()
    .replace(/^<+|>+$/g, '')
    .toLowerCase();
}

/** Załącznik pokazany w treści (`isInline` albo przywołany przez `cid:`). */
export function isInlineAttachment(att: RaRecord, html: string | null | undefined): boolean {
  if (att.isInline === true) return true;
  const cid = normalizeContentId(att.contentId as string | undefined);
  return !!cid && !!html && html.toLowerCase().includes(`cid:${cid}`);
}

export const SCAN_STATUS_LABELS: Record<string, string> = {
  pending: 'czeka na skan',
  clean: 'skan: czysty',
  infected: 'skan: wykryto zagrożenie',
  skipped: 'bez skanu',
};

// ── treść HTML ──────────────────────────────────────────────────────────────

export interface PreparedHtml {
  /** Cały dokument do `srcDoc`, z polityką treści na początku. */
  html: string;
  /** Ile obrazów z sieci zablokowano (przy `allowRemote = false`). */
  blockedRemote: number;
  /** Obrazy `cid:` bez pliku (jeszcze się ładuje albo nie ma go w mailu). */
  missingCid: string[];
}

const REMOTE = /^(https?:)?\/\//i;

/**
 * Treść HTML maila do ramki podglądu: bez skryptów, obsług zdarzeń,
 * formularzy i osadzonych obiektów (DOMPurify), obrazy `cid:` podmienione na
 * pliki z naszej trasy (adresy `data:` z `cidImages`, pobrane z
 * uprawnieniem), obrazy z sieci domyślnie zablokowane — „Pokaż obrazy
 * z sieci” je włącza. Polityka treści w dokumencie (`img-src`) blokuje sieć
 * także wtedy, gdy obraz siedzi w stylu (`background: url(…)`); ramka ma
 * i tak `sandbox` bez skryptów.
 */
export function prepareEmailHtml(
  raw: string,
  opts: { cidImages?: Record<string, string>; allowRemote?: boolean } = {},
): PreparedHtml {
  const cidImages = opts.cidImages ?? {};
  const allowRemote = opts.allowRemote === true;
  let blockedRemote = 0;
  const missingCid: string[] = [];
  const purifier = DOMPurify(window);
  purifier.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    if (!el.getAttribute) return;
    for (const attr of ['src', 'background', 'poster']) {
      const v = el.getAttribute(attr);
      if (!v) continue;
      const value = v.trim();
      if (/^cid:/i.test(value)) {
        let cid = value.slice(4);
        try {
          cid = decodeURIComponent(cid);
        } catch {
          /* zostaje jak w treści */
        }
        const url = cidImages[normalizeContentId(cid)];
        if (url) el.setAttribute(attr, url);
        else {
          el.removeAttribute(attr);
          el.setAttribute('data-missing-cid', normalizeContentId(cid));
          missingCid.push(normalizeContentId(cid));
        }
      } else if (REMOTE.test(value) && !allowRemote) {
        el.removeAttribute(attr);
        el.setAttribute('data-blocked-src', '1');
        blockedRemote += 1;
      }
    }
    if (el.hasAttribute('srcset') && !allowRemote) {
      el.removeAttribute('srcset');
    }
  });
  const clean = purifier.sanitize(raw, {
    WHOLE_DOCUMENT: true,
    FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'iframe', 'object', 'embed', 'base', 'link', 'meta'],
    ADD_URI_SAFE_ATTR: [],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ADD_DATA_URI_TAGS: ['img'],
  }) as unknown as string;
  purifier.removeAllHooks();
  const imgSrc = allowRemote ? "data: https: http:" : 'data:';
  const csp = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'; font-src data:">`;
  const html = /<head[^>]*>/i.test(clean)
    ? clean.replace(/<head([^>]*)>/i, `<head$1>${csp}`)
    : `<!doctype html><html><head>${csp}</head><body>${clean}</body></html>`;
  return { html, blockedRemote, missingCid };
}
